import React, { useState, useEffect, useRef, useCallback } from "react";
import { DateTime } from "luxon";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { setRole, updateSession } from "../../store/actions/sessionsActions";
import { setLocalStorageItem } from "../../store/actions/localStorageActions";
import {
  Box,
  Stack,
  Link,
  Typography,
  Grid2 as Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";

const EmailFaAuthPass = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [pageLoading, setPageLoading] = useState(true);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tokenFromQuery = queryParams.get("token");
  const rememberFromQuery = queryParams.get("remember");

  let processDataRef = useRef(null);

  const process_data = useCallback(async () => {
    try {
      const tokenResponse = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/auth/2fa/email-pass-verified`,
        {
          token: tokenFromQuery,
          remember_token: rememberFromQuery,
        },
        { withCredentials: true }
      );

      if (tokenResponse?.data?.success === false) {
        navigate("/login");
      } else {
        if (rememberFromQuery === "true") {
          dispatch(
            setLocalStorageItem("ujsffq", {
              email: tokenResponse?.data?.data.email,
            })
          );
        }
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/auth/2fa/clear-email-verify`,
          {
            token: tokenFromQuery,
          }
        );
        localStorage.setItem("emailVerified", "true");
        await dispatch(updateSession(tokenResponse?.data?.data));

        if (tokenResponse?.data?.data?.sessionType === "notarySession") {
          navigate("/notary/dashboard");
        } else if (
          tokenResponse?.data?.data?.sessionType === "userSession" ||
          tokenResponse?.data?.data?.sessionType === "subUserSession" ||
          tokenResponse?.data?.data?.sessionType === "privateProviderSession"
        ) {
          const role = tokenResponse?.data?.data?.returnRole;
          const expiryDuration = parseInt(process.env.REACT_APP_SESSION_EXP);
          const currentUtcTime = DateTime.utc();
          const expiryTime = currentUtcTime
            .plus({ seconds: expiryDuration })
            .toSeconds();

          sessionStorage.setItem("__nocCreator__r_rid", JSON.stringify(role));
          sessionStorage.setItem("exp", Math.floor(expiryTime));
          await dispatch(setRole(role));
          navigate("/dashboard");
        }
      }
      setPageLoading(false);
    } catch (error) {
      console.error("Error", error);
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tokenFromQuery) {
      navigate("/login");
      return;
    }

    if (!processDataRef?.current) {
      processDataRef.current = true;
      process_data();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResendCode = async () => {
    navigate("/dashboard");
  };

  return (
    <>
      {pageLoading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress color="warning" size="10rem" />
          </Box>
        </>
      ) : (
        <Box
          sx={
            isSmallScreen
              ? {
                  justifyContent: "center",
                  overflow: "auto",
                  width: "100%",
                  height: "100vh",
                }
              : {
                  backgroundImage: "url('/img/website_img/homeImg.jpeg')",
                  backgroundRepeat: "repeat",
                  width: "100%",
                  flex: "1 1 auto",
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  height: "100vh",
                }
          }
        >
          <Box
            sx={
              isSmallScreen
                ? {
                    display: "flex",
                    alignItems: "center",
                    flex: "1 1 auto",
                    justifyContent: "center",
                    backgroundColor: "#fff",
                    py: "10px",
                    width: "100%",
                  }
                : {
                    overflowY: "auto",
                    backgroundColor: "#fff",
                    maxWidth: 900,
                    maxHeight: "95vh",
                    borderRadius: "25px",
                    px: 3,
                    py: "10px",
                    width: "100%",
                    pt: "2rem",
                  }
            }
          >
            <div
              style={
                isSmallScreen
                  ? { width: "100%", padding: "1rem" }
                  : { padding: "1rem" }
              }
            >
              <Stack spacing={1} sx={{ mb: 3 }}>
                <Box
                  sx={{ cursor: "pointer" }}
                  display="flex"
                  justifyContent="center"
                  onClick={() => {
                    navigate("/");
                  }}
                >
                  <img
                    style={{
                      width: "10rem",
                      marginLeft: "auto",
                      marginRight: "auto",
                      marginTop: "-9px",
                    }}
                    alt="Permit Rockstar Logo"
                    src="/img/website_img/PermitRockstar.png"
                  />
                </Box>
              </Stack>
              <Typography
                sx={{ mb: 2 }}
                color="primary"
                variant="h4"
                align="center"
              >
                Two Factor Authentication was Successful
              </Typography>
              <Typography color="primary" variant="h5" align="center">
                Redirecting to Dashboard
              </Typography>

              <Grid container justifyContent="center" spacing={2}>
                {images.map((image, index) => (
                  <Grid size={{ sm: 12, lg: 4 }} key={index}>
                    <Box
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: "transparent",
                        flexDirection: "column",
                        cursor: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Box sx={{ pb: 3, pt: 1 }}>
                        <img
                          alt={image.alt}
                          src={image.src}
                          width={image.width}
                        />
                      </Box>
                    </Box>
                  </Grid>
                ))}

                <>
                  <Grid container justifyContent="center" mb={2} mt={5}>
                    <Grid size={{ sm: 12 }} sx={{ width: "100%" }}>
                      <Typography
                        color="primary"
                        variant="body1"
                        align="center"
                        sx={{ mb: 5 }}
                      >
                        In 5 seconds, you will be redirected to the relevant
                        page. If you do not wish to wait, please click the
                        button below.
                      </Typography>
                    </Grid>
                    <Grid size={{ sm: 12 }} sx={{ mt: 2, minHeight: "1.5rem" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                        onClick={handleResendCode}
                      >
                        <Typography
                          component={Link}
                          variant="body1"
                          sx={{ textAlign: "center", cursor: "pointer" }}
                          color="primary"
                          fontWeight="bold"
                        >
                          Go to Dashboard
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              </Grid>
            </div>
          </Box>
        </Box>
      )}
    </>
  );
};

const images = [
  {
    alt: "authenticator",
    src: "/img/logos/2falogo.png",
    // width: 167,
    width: 250,
  },
];

export default EmailFaAuthPass;
